import { publicApi } from "@api";
import { envConfig } from "@configs/env";
import { useLoginSubmit } from "@hooks/useLoginSubmit";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { getStoredCredential } from "@utils/credentials";
import { resolveRegisterRequestError } from "@views/AuthLoginPage/utils";
import {
  APIErrorCodes,
  AuthToken,
  Button,
  CodeField,
  cn,
  standardizePhoneNumber,
  useCallbackRef,
  useDateAvailCheck,
  useHandleApiResponse,
} from "kz-ui-sdk";
import { throttle } from "lodash-es";
import { PropsWithChildren, useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import FormTitle from "../FormTitle";

interface FormPINProps extends PropsWithChildren {
  phone: string;
  displayMode?: "compact" | "full";
  onForgotSecret?: () => void;
  onLoginSuccess?: (token?: AuthToken) => void;
  isInputNum?: boolean;
}

const PIN_LENGTH = 4;

const FormPIN = ({
  phone,
  displayMode = "compact",
  onForgotSecret: onForgotPIN,
  onLoginSuccess,
  isInputNum = true,
}: FormPINProps) => {
  const [PINValue, setPINValue] = useState("");
  const { phonePrefix } = useMemberSettings();
  const { handleApiResponse } = useHandleApiResponse({
    toast,
  });

  const [createForgotPwdRequest, { isLoading: isCreatingForgotPwd }] = publicApi.useCreateForgotPasswordMutation();
  const { t } = useTranslation();
  const [errorMsgPIN, setErrorMsgPIN] = useState<string>();
  const cbRefOnForgotPIN = useCallbackRef(onForgotPIN);

  const { check: checkRequestOTP } = useDateAvailCheck(
    standardizePhoneNumber(getStoredCredential().phone ?? "", phonePrefix),
  );

  const doForgotPIN = useCallback(async () => {
    toast.dismiss();
    let allowRedirect = true;
    if (checkRequestOTP()) {
      const response = await createForgotPwdRequest({
        phone: standardizePhoneNumber(phone, phonePrefix),
        accountId: envConfig.accountId,
      });

      if (response.error) {
        const errorData = (response.error as FetchBaseQueryError).data as {
          error: { message: string };
        };
        const errorMessage = errorData?.error?.message;

        if (errorMessage === APIErrorCodes.VerifyRequest.RateLimited || errorMessage?.startsWith("unauthorized")) {
          allowRedirect = false;
        }
      }

      handleApiResponse(response, {
        successMessage: t("OTP sent to your phone!"),
        toastError: false,
        onError: (error) => {
          const message = resolveRegisterRequestError(error, t);
          if (message) {
            toast.error(message);
          }
        },
      });

      if (response.error) {
        const errorData = (response.error as FetchBaseQueryError).data as {
          error: { message: string };
        };
        const errorMessage = errorData?.error?.message;

        if (errorMessage === APIErrorCodes.VerifyRequest.RateLimited || errorMessage?.startsWith("unauthorized")) {
          allowRedirect = false;
        }
      }
    }
    if (allowRedirect) {
      cbRefOnForgotPIN?.();
    }
  }, [cbRefOnForgotPIN, checkRequestOTP, createForgotPwdRequest, handleApiResponse, phone, t, phonePrefix]);

  const {
    handleLogin,
    isLoading: isLoginLoading,
    isError: isLoginError,
  } = useLoginSubmit({
    onLoginSuccess,
    onLoginError: (error) => {
      setPINValue("");
    },
    onForgotSecret: doForgotPIN,
    setInlineError: setErrorMsgPIN,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttledLogin = useCallback(
    throttle(
      (pin: string) => {
        handleLogin(phone, pin);
      },
      1000,
      { leading: true, trailing: false },
    ),
    [phone],
  );

  const handleOnPINComplete = useCallback(
    (pin: string) => {
      throttledLogin(pin);
    },
    [throttledLogin],
  );

  const isFormLoading = useMemo(() => {
    return isLoginLoading || isCreatingForgotPwd;
  }, [isLoginLoading, isCreatingForgotPwd]);

  return (
    <div
      className={cn("flex w-full flex-col items-center justify-between", {
        "!px-0": displayMode === "compact",
      })}
    >
      <FormTitle
        title={phone}
        subTitle={t("Enter your PIN to Log in")}
      />
      <div className="mt-5">
        <CodeField
          isInputNum={isInputNum}
          key={errorMsgPIN}
          length={PIN_LENGTH}
          type={"password"}
          shouldAutoFocus
          disabled={isFormLoading}
          value={PINValue}
          onComplete={handleOnPINComplete}
          error={!!errorMsgPIN || isLoginError}
          helperText={errorMsgPIN}
          helperTextClasses={{
            className: "!text-center",
          }}
          className="flex flex-col items-center"
        />
        <Button
          className="mt-6 w-full"
          id="btn-next"
          variant="primary"
          size="lg"
          onClick={() => handleOnPINComplete(PINValue)}
          loading={isFormLoading}
        >
          {t("Login")}
        </Button>
      </div>
      <Button
        size="sm"
        className="mt-6 !w-fit px-3"
        variant="secondary"
        onClick={doForgotPIN}
        disabled={isFormLoading}
      >
        {t("forgot pin")}
      </Button>
    </div>
  );
};

export default FormPIN;
